<template>
    <div id="category">
        <USPBanner v-if="uspBannerVisible" />

        <Teaser
            v-if="showTeaser"
            class="category-teaser"
            :backgroundUrl="teaserBackground"
            :gift-box-banner-visible="category.giftBoxBannerVisible"
            :alt="$getSafe(category, 'pageData.title')"
            height="380"
        >
            <DetailsTeaser :title="title" :subtitle="subtitle" class="category-teaser" />
        </Teaser>

        <div class="category-content container-fluid main">
            <div class="row">
                <template v-if="breadcrumb.length">
                    <!---this template forces breadcrumbs to appear properly before the sidebar on mobile and desktop-->
                    <div v-if="sidebar" class="sidebar__column"></div>

                    <div class="container">
                        <Breadcrumbs :breadcrumbs="breadcrumb" />
                    </div>

                    <div style="width: 100% !important"></div>
                </template>

                <Sidebar v-if="sidebar" :sidebar="sidebar" />

                <div class="container">
                    <h1 v-if="!showTeaser && title" class="category__title page-title">{{ title }}</h1>

                    <media-only v-if="subtitle" mobile>
                        <div v-html="subtitle" class="mb-3"></div>
                    </media-only>

                    <div v-if="noProductsAndContent" class="category__empty-alert alert alert-info">
                        {{ $t('category.noProducts') }}
                    </div>

                    <template v-else>
                        <Grid
                            v-for="(grid, index) in grids"
                            :key="'grid' + index"
                            :grid="grid"
                            :first="index === 0"
                            allow-resizing
                            :class="{ 'mt-2': index === 0 }"
                        >
                        </Grid>

                        <PageContent
                            v-if="content"
                            :content="content"
                            :small="!!grids.length"
                            class="category-content-text"
                        >
                        </PageContent>
                    </template>
                </div>
            </div>
        </div>

        <ExitIntentNewsletter v-if="$store.state.globalMeta.newsletterFormEnabled" />
    </div>
</template>

<script>
import Teaser from '@/components/teasers/Teaser';
import DetailsTeaser from '@/components/teasers/DetailsTeaser';
import Grid from '@/components/grid/Grid';
import PageContent from '@/components/PageContent';
import headMixin from '@/mixins/getHead';
import extraScriptCode from '@/mixins/extraScriptCode';
import advertisable from '@/mixins/advertisable';
import fetchResource from '@/utils/fetchResource';
import MediaOnly from '@/components/MediaOnly';
import ExitIntentNewsletter from '@/components/modals/ExitIntentNewsletter';
import Sidebar from '@/components/navigation/Sidebar';
import Breadcrumbs from '@/components/navigation/Breadcrumbs';

export default {
    name: 'Category',
    layout: ({ layout }) => layout,
    async fetch(context) {
        await fetchResource(context, context.store.dispatch('getCategory', context.params.category));
    },
    mixins: [headMixin, extraScriptCode('category'), advertisable('category')],
    head() {
        const pageData = this.category.pageData || {};
        return this.getHead({
            pageTitle: pageData.metaTitle,
            noindex: pageData.noindex,
            keywords: pageData.metaKeywords,
            metaDescription: pageData.metaDescription,
            canonicalUrl: pageData.canonicalUrl,
            hrefLangs: pageData.hrefLangs
        });
    },
    components: {
        Breadcrumbs,
        Sidebar,
        ExitIntentNewsletter,
        MediaOnly,
        Teaser,
        DetailsTeaser,
        Grid,
        PageContent
    },
    computed: {
        category() {
            return this.$store.state.category || {};
        },
        teaserBackground() {
            //feel free to add items, making a carousel of teasers
            return this.$getSafe(this.category, 'pageData.teaserImageUrl');
        },
        title() {
            return this.$getSafe(this.category, 'pageData.title');
        },
        subtitle() {
            return this.$getSafe(this.category, 'pageData.text');
        },
        sidebar() {
            return this.$getSafe(this.category, 'pageData.sidebar');
        },
        breadcrumb() {
            return this.category.breadcrumb || [];
        },
        grids() {
            return this.category.grids || [];
        },
        content() {
            return this.$getSafe(this.category, 'pageData.content');
        },
        noProductsAndContent() {
            return (
                !this.grids.some(g => {
                    return !!(g.blocks || []).length || !!g.content || !!g.jsonData;
                }) &&
                !this.content &&
                !this.$cmsTool
            );
        },
        showTeaser() {
            return this.$store.state.ident !== 'kurzinurlaub';
        }
    },
    methods: {
        sendGTMEvent() {
            this.$gtm && this.$gtm.pushPageView('category');
        }
    },
    beforeMount() {
        this.sendGTMEvent();
    }
};
</script>

<style scoped lang="scss">
@use '@/styles/variables';
@use '@/styles/mixins';

#category {
    .category-teaser ::v-deep a {
        color: hsl(0, 0%, 100%);
    }

    .category-teaser ::v-deep .teaser-subtitle {
        display: block;
    }

    .category-content {
        margin-top: variables.$grid-padding;
    }

    @include mixins.mobile-only {
        .category-teaser ::v-deep .teaser-subtitle {
            display: none;
        }
    }

    .category__empty-alert {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 200px;
        margin-top: 15px;
    }
}
</style>
